import { __rest } from 'tslib';
import { Logger } from '@notificare/web-logger';

const logger = new Logger('notificare/network');

function sleep(milliseconds) {
    return new Promise((resolve) => {
        setTimeout(resolve, milliseconds);
    });
}

function base64Encode(data) {
    // eslint-disable-next-line no-restricted-globals
    return self.btoa(data);
}

class NotificareNetworkRequestError extends Error {
    constructor(response) {
        super(`Failed to fetch a resource with response code '${response.status}'.`);
        this.response = response;
        Object.setPrototypeOf(this, NotificareNetworkRequestError.prototype);
    }
}

async function request(params) {
    var _a;
    const { url, method = 'GET' } = params;
    logger.debug(`${method} ${url}`);
    const retries = (_a = params.retries) !== null && _a !== void 0 ? _a : defaults.retries;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
        let response;
        try {
            // eslint-disable-next-line no-await-in-loop
            response = await fetch(url, {
                method,
                body: getRequestBody(params),
                headers: getRequestHeaders(params),
            });
            // The request completed successfully and the response is OK.
            // Otherwise, don't retry and throw immediately.
            if (response.ok)
                return response;
        }
        catch (e) {
            logger.warning(`Request attempt #${attempt + 1} failed.`, e);
            if (attempt < retries) {
                const delay = calculateRetryDelayInMilliseconds(attempt, params);
                logger.debug(`Retrying in ${delay} milliseconds...`);
                // eslint-disable-next-line no-await-in-loop
                await sleep(delay);
            }
        }
        // Having a response after the try catch means the request completed successfully
        // but there's something wrong with it (ie bad request).
        if (response)
            throw new NotificareNetworkRequestError(response);
    }
    logger.error('Request exceeded maximum retries.');
    throw new Error('Request exceeded maximum retries.');
}
function getRequestHeaders(params) {
    const headers = new Headers();
    headers.set('Accept', 'application/json');
    if (params.body)
        headers.set('Content-Type', 'application/json');
    // if (params.formData) headers.set('Content-Type', 'multipart/form-data');
    const authorizationHeader = getAuthorizationHeader(params);
    if (authorizationHeader)
        headers.set('Authorization', authorizationHeader);
    return headers;
}
function getAuthorizationHeader({ authorization }) {
    if (!authorization)
        return undefined;
    const { basic } = authorization;
    if (basic) {
        const encoded = base64Encode(`${basic.username}:${basic.password}`);
        return `Basic ${encoded}`;
    }
    const { bearer } = authorization;
    if (bearer) {
        return `Bearer ${bearer}`;
    }
    return undefined;
}
function getRequestBody(params) {
    if (params.body)
        return JSON.stringify(params.body);
    if (params.formData)
        return params.formData;
    return undefined;
}
function calculateRetryDelayInMilliseconds(attempt, params) {
    var _a;
    const retryDelay = (_a = params.retryDelay) !== null && _a !== void 0 ? _a : defaults.retryDelay;
    if (typeof retryDelay === 'function') {
        return retryDelay(attempt);
    }
    return retryDelay;
}
const defaults = {
    retries: 3,
    retryDelay: (attempt) => 2 ** attempt * 1000, // 1000, 2000, 4000
};

async function cloudRequest(params) {
    const { environment, path, searchParams } = params, rest = __rest(params, ["environment", "path", "searchParams"]);
    const url = getCloudUrl(environment);
    url.pathname = path;
    searchParams === null || searchParams === void 0 ? void 0 : searchParams.forEach((value, key) => {
        url.searchParams.set(key, value);
    });
    return request(Object.assign(Object.assign({}, rest), { url, authorization: getCloudRequestAuthorization(environment) }));
}
function getCloudUrl({ cloudHost }) {
    return new URL(`https://${cloudHost}`);
}
function getCloudRequestAuthorization({ applicationKey, applicationSecret, }) {
    return {
        basic: {
            username: applicationKey,
            password: applicationSecret,
        },
    };
}

async function fetchCloudApplication(params) {
    const { language } = params, rest = __rest(params, ["language"]);
    const searchParams = new URLSearchParams();
    if (language)
        searchParams.set('language', language);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: '/api/application/info', searchParams }));
    return response.json();
}

async function fetchCloudAssetGroup(params) {
    const { deviceId, userId, group } = params, rest = __rest(params, ["deviceId", "userId", "group"]);
    const searchParams = new URLSearchParams({ deviceID: deviceId });
    if (userId)
        searchParams.set('userID', userId);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: `/api/asset/forgroup/${encodeURIComponent(group)}`, searchParams }));
    return response.json();
}

async function createCloudDevice(params) {
    const { payload } = params, rest = __rest(params, ["payload"]);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'POST', path: `/api/push`, body: payload }));
    return response.json();
}
async function upgradeToLongLivedCloudDevice(params) {
    const { payload } = params, rest = __rest(params, ["payload"]);
    return cloudRequest(Object.assign(Object.assign({}, rest), { method: 'POST', path: '/api/push', body: payload }));
}
async function updateCloudDevice(params) {
    const { deviceId, payload } = params, rest = __rest(params, ["deviceId", "payload"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'PUT', path: `/api/push/${encodeURIComponent(deviceId)}`, body: payload }));
}
async function deleteCloudDevice(params) {
    const { deviceId } = params, rest = __rest(params, ["deviceId"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'DELETE', path: `/api/push/${encodeURIComponent(deviceId)}` }));
}
async function fetchCloudDeviceTags(params) {
    const { deviceId } = params, rest = __rest(params, ["deviceId"]);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: `/api/push/${encodeURIComponent(deviceId)}/tags` }));
    return response.json();
}
async function addCloudDeviceTags(params) {
    const { deviceId, tags } = params, rest = __rest(params, ["deviceId", "tags"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'PUT', path: `/api/push/${encodeURIComponent(deviceId)}/addtags`, body: { tags } }));
}
async function removeCloudDeviceTags(params) {
    const { deviceId, tags } = params, rest = __rest(params, ["deviceId", "tags"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'PUT', path: `/api/push/${encodeURIComponent(deviceId)}/removetags`, body: { tags } }));
}
async function clearCloudDeviceTags(params) {
    const { deviceId } = params, rest = __rest(params, ["deviceId"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'PUT', path: `/api/push/${encodeURIComponent(deviceId)}/cleartags` }));
}
async function fetchCloudDeviceDoNotDisturb(params) {
    const { deviceId } = params, rest = __rest(params, ["deviceId"]);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: `/api/push/${encodeURIComponent(deviceId)}/dnd` }));
    return response.json();
}
async function fetchCloudDeviceUserData(params) {
    const { deviceId } = params, rest = __rest(params, ["deviceId"]);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: `/api/push/${encodeURIComponent(deviceId)}/userdata` }));
    return response.json();
}
async function registerCloudTestDevice(params) {
    const { deviceId, nonce } = params, rest = __rest(params, ["deviceId", "nonce"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'PUT', path: `/api/support/testdevice/${encodeURIComponent(nonce)}`, body: { deviceID: deviceId } }));
}

async function fetchCloudDeviceInbox(params) {
    var _a, _b;
    const { deviceId, skip, limit, since } = params, rest = __rest(params, ["deviceId", "skip", "limit", "since"]);
    const searchParams = new URLSearchParams({
        skip: (_a = skip === null || skip === void 0 ? void 0 : skip.toString()) !== null && _a !== void 0 ? _a : '0',
        limit: (_b = limit === null || limit === void 0 ? void 0 : limit.toString()) !== null && _b !== void 0 ? _b : '100',
    });
    if (since)
        searchParams.set('since', since);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: `/api/notification/inbox/fordevice/${encodeURIComponent(deviceId)}`, searchParams }));
    return response.json();
}
async function removeCloudDeviceInboxItem(params) {
    const { id } = params, rest = __rest(params, ["id"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'DELETE', path: `/api/notification/inbox/${encodeURIComponent(id)}` }));
}
async function markCloudDeviceInboxAsRead(params) {
    const { deviceId } = params, rest = __rest(params, ["deviceId"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'PUT', path: `/api/notification/inbox/fordevice/${encodeURIComponent(deviceId)}` }));
}
async function clearCloudDeviceInbox(params) {
    const { deviceId } = params, rest = __rest(params, ["deviceId"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'DELETE', path: `/api/notification/inbox/fordevice/${encodeURIComponent(deviceId)}` }));
}

async function fetchCloudDynamicLink(params) {
    const { deviceId, url } = params, rest = __rest(params, ["deviceId", "url"]);
    const searchParams = new URLSearchParams({ platform: 'Web' });
    if (deviceId)
        searchParams.set('deviceID', deviceId);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: `/api/link/dynamic/${encodeURIComponent(url)}`, searchParams }));
    return response.json();
}

async function createCloudEvent(params) {
    const { payload } = params, rest = __rest(params, ["payload"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'POST', path: `/api/event`, body: payload }));
}

async function fetchCloudInAppMessage(params) {
    const { context, deviceId } = params, rest = __rest(params, ["context", "deviceId"]);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: `/api/inappmessage/forcontext/${encodeURIComponent(context)}`, searchParams: new URLSearchParams({ deviceID: deviceId }) }));
    return response.json();
}

async function callCloudNotificationWebhook(params) {
    const { payload } = params, rest = __rest(params, ["payload"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'POST', path: `/api/notification/webhook`, body: payload }));
}
async function uploadCloudNotificationReplyMedia(params) {
    const { media } = params, rest = __rest(params, ["media"]);
    const formData = new FormData();
    formData.append('file', media);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'POST', path: '/api/upload/reply', formData }));
    return response.json();
}
async function createCloudNotificationReply(params) {
    const { payload } = params, rest = __rest(params, ["payload"]);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'POST', path: '/api/reply', body: payload }));
}

async function fetchCloudNotification(params) {
    const { id } = params, rest = __rest(params, ["id"]);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: `/api/notification/${encodeURIComponent(id)}` }));
    return response.json();
}

async function fetchCloudPass(params) {
    const { serial } = params, rest = __rest(params, ["serial"]);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: `/api/pass/forserial/${encodeURIComponent(serial)}` }));
    return response.json();
}
async function fetchCloudPassSaveLinks(params) {
    const { serial } = params, rest = __rest(params, ["serial"]);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: `/api/pass/savelinks/${encodeURIComponent(serial)}` }));
    return response.json();
}

async function fetchCloudUserInboxNotification(params) {
    const { id, deviceId } = params, rest = __rest(params, ["id", "deviceId"]);
    const encodedId = encodeURIComponent(id);
    const encodedDeviceId = encodeURIComponent(deviceId);
    const response = await cloudRequest(Object.assign(Object.assign({}, rest), { path: `/api/notification/userinbox/${encodedId}/fordevice/${encodedDeviceId}` }));
    return response.json();
}
async function removeCloudUserInboxItem(params) {
    const { id, deviceId } = params, rest = __rest(params, ["id", "deviceId"]);
    const encodedId = encodeURIComponent(id);
    const encodedDeviceId = encodeURIComponent(deviceId);
    await cloudRequest(Object.assign(Object.assign({}, rest), { method: 'DELETE', path: `/api/notification/userinbox/${encodedId}/fordevice/${encodedDeviceId}` }));
}

export { NotificareNetworkRequestError, addCloudDeviceTags, callCloudNotificationWebhook, clearCloudDeviceInbox, clearCloudDeviceTags, createCloudDevice, createCloudEvent, createCloudNotificationReply, deleteCloudDevice, fetchCloudApplication, fetchCloudAssetGroup, fetchCloudDeviceDoNotDisturb, fetchCloudDeviceInbox, fetchCloudDeviceTags, fetchCloudDeviceUserData, fetchCloudDynamicLink, fetchCloudInAppMessage, fetchCloudNotification, fetchCloudPass, fetchCloudPassSaveLinks, fetchCloudUserInboxNotification, markCloudDeviceInboxAsRead, registerCloudTestDevice, removeCloudDeviceInboxItem, removeCloudDeviceTags, removeCloudUserInboxItem, request, updateCloudDevice, upgradeToLongLivedCloudDevice, uploadCloudNotificationReplyMedia };
//# sourceMappingURL=index.js.map
